import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useOnboardingPortalTheme } from './onboardingBrand';
import { useOnboardingExit } from './onboardingExit';
import { QueryKey } from '@/Config/api/queryKey';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import type { AIAssistantsData } from '@/Domain/components/TopBar/types';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useLogout } from '@/Domain/hooks/Auth/useLogout';
import { getCurrentScenarioId } from '@/context';

type ExitVariant = 'delete' | 'logout';

export const LeaveOnboardingButton = () => {
  const { t } = useTranslation('getting_started');
  const { capture } = usePostHogAnalytics();
  const portalTheme = useOnboardingPortalTheme();
  const { canFinish, finish } = useOnboardingExit();
  const { data: aiAssistants } = useQuery<AIAssistantsData>({
    queryKey: [QueryKey.aiAssistants.get],
  });
  const { isActivated } = useSubscription();
  const [openVariant, setOpenVariant] = useState<ExitVariant | null>(null);
  const close = () => setOpenVariant(null);

  // The URL agent is the one the DELETE hits; SCENARIO_ID is '' after a social-login entry.
  const currentScenarioId = getCurrentScenarioId();
  const fallbackAgent = currentScenarioId
    ? (aiAssistants?.all_ai_assistants ?? []).find((a) => a.scenario_id !== currentScenarioId)
    : undefined;
  const hasDeleteRole = !!aiAssistants?.current_ai_assistant?.permissions?.delete_ai_agent;
  // Mirrors both /delete_implementation gates; offering it on either alone 403s.
  const canDelete = !!fallbackAgent && hasDeleteRole && isActivated('can_delete');

  const {
    mutate: deleteAgent,
    isPending: isDeleting,
    isSuccess: deleteSucceeded,
  } = useMutation<void, unknown, void>({
    mutationKey: [QueryKey.implementation.delete],
    onSuccess: () => {
      // Hard navigate so the app shell re-inits against the fallback scenario_id.
      window.location.replace(fallbackAgent ? `/i/${fallbackAgent.scenario_id}` : '/');
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        t('leave_onboarding.error');
      toast.error(message);
    },
  });
  // Stay loading from click through the post-success redirect, so it can't re-submit.
  const isLeavingViaDelete = isDeleting || deleteSucceeded;

  const { handleLogout, isLoggingOut } = useLogout();

  const triggerLabel = canFinish
    ? t('leave_onboarding.finish_button')
    : t('leave_onboarding.button');

  const handleTriggerClick = () => {
    if (canFinish) {
      if (finish()) capture(PostHogEventEnum.GettingStartedLeftViaFinish);
      return;
    }
    // Frozen here: a plan response must not turn an open logout dialog into a delete one.
    const variant: ExitVariant = canDelete ? 'delete' : 'logout';
    capture(PostHogEventEnum.GettingStartedLeaveOpened, { variant });
    setOpenVariant(variant);
  };
  const handleCancel = () =>
    capture(PostHogEventEnum.GettingStartedLeaveCanceled, { variant: openVariant });
  const handleConfirmDelete = () => {
    // Fire before the request so the abandon signal lands even if the redirect cuts it short.
    capture(PostHogEventEnum.GettingStartedLeftViaDelete, {
      fallback_scenario_id: fallbackAgent?.scenario_id,
    });
    deleteAgent();
  };
  const handleConfirmLogout = () => {
    // Fire before logout, which calls posthog.reset() and clears the user identity.
    capture(PostHogEventEnum.GettingStartedLeftViaLogout);
    handleLogout();
  };

  // Wait for the assistants query so the exit variant isn't mis-derived from empty state.
  if (!aiAssistants && !canFinish) return null;

  return (
    <>
      <IconButton
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleTriggerClick}
        aria-label={triggerLabel}
        title={triggerLabel}
        className="absolute top-5 right-5 z-20"
        data-testid="gs-leave-onboarding-button"
      >
        <X className="!size-5" />
      </IconButton>
      {openVariant === 'delete' ? (
        <AlertDialog open onOpenChange={(next) => !isLeavingViaDelete && !next && close()}>
          <AlertDialogContent className={portalTheme}>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('leave_onboarding.dialog.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('leave_onboarding.dialog.description', { name: fallbackAgent?.name })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel asChild disabled={isLeavingViaDelete}>
                <Button
                  variant="secondary"
                  disabled={isLeavingViaDelete}
                  onClick={handleCancel}
                  data-testid="gs-leave-onboarding-cancel"
                >
                  {t('leave_onboarding.dialog.cancel')}
                </Button>
              </AlertDialogCancel>
              <Button
                variant="danger"
                loading={isLeavingViaDelete}
                onClick={handleConfirmDelete}
                data-testid="gs-leave-onboarding-confirm"
              >
                {t('leave_onboarding.dialog.confirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : openVariant === 'logout' ? (
        <AlertDialog open onOpenChange={(next) => !isLoggingOut && !next && close()}>
          <AlertDialogContent className={portalTheme}>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('leave_onboarding.logout_dialog.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('leave_onboarding.logout_dialog.description')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel asChild disabled={isLoggingOut}>
                <Button
                  variant="secondary"
                  disabled={isLoggingOut}
                  onClick={handleCancel}
                  data-testid="gs-leave-onboarding-cancel"
                >
                  {t('leave_onboarding.logout_dialog.cancel')}
                </Button>
              </AlertDialogCancel>
              <Button
                variant="dark"
                loading={isLoggingOut}
                onClick={handleConfirmLogout}
                data-testid="gs-leave-onboarding-logout-confirm"
              >
                {t('leave_onboarding.logout_dialog.confirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </>
  );
};
